import getRemoteConfigValue from './remoteConfig';

export const COURSE_ENTRY_VIDEOS_RC_KEY_WEB = 'course_entry_videos_web';
export const COURSE_ENTRY_VIDEOS_RC_KEY_MOBILE = 'course_entry_videos';
export const COURSE_ENTRY_VIDEO_SEEN_STORAGE_PREFIX =
  'quiklearn_course_entry_video_seen_v1_';

function toSafeString(value) {
  if (value === null || typeof value === 'undefined') return '';
  return String(value).trim();
}

function normalizeToken(value) {
  return toSafeString(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function normalizeStorageToken(value, fallback) {
  const normalized = toSafeString(value)
    .toLowerCase()
    .replace(/[^a-z0-9:_-]/g, '_');
  return normalized || fallback;
}

function parseGradeSubjectFromText(input) {
  const text = toSafeString(input);
  if (!text) return {};

  // Accept inputs like:
  // - "Science 3"
  // - "Science 3 - Unit 1 - Lesson 1"
  // - "history_8"
  const match = text.match(/([A-Za-z]+)[^0-9]*([0-9]{1,2})/);
  if (!match) return {};

  return {
    subject: match[1],
    grade: match[2],
  };
}

function normalizeRawEntries(parsedConfig) {
  if (Array.isArray(parsedConfig)) {
    return parsedConfig;
  }

  if (!parsedConfig || typeof parsedConfig !== 'object') {
    return [];
  }

  // Also support object-map configs:
  // {
  //   "science_3": { ... },
  //   "history_6": "https://youtube.com/shorts/..."
  // }
  return Object.entries(parsedConfig).map(([key, value]) => {
    if (value && typeof value === 'object') {
      return { id: key, ...value, key };
    }
    return { id: key, key, video_url: value };
  });
}

function normalizeVideoEntry(rawEntry, index) {
  if (!rawEntry || typeof rawEntry !== 'object') return null;
  if (rawEntry.enabled === false) return null;

  const match = rawEntry.match && typeof rawEntry.match === 'object'
    ? rawEntry.match
    : {};

  const videoInput = toSafeString(
    rawEntry.video_url ||
      rawEntry.videoUrl ||
      rawEntry.video_id ||
      rawEntry.videoId ||
      rawEntry.youtube_url ||
      rawEntry.youtubeUrl ||
      rawEntry.url,
  );

  if (!videoInput) return null;

  const curriculumId = toSafeString(
    rawEntry.curriculum_id ||
      rawEntry.curriculumId ||
      rawEntry.course_id ||
      rawEntry.courseId ||
      match.curriculum_id ||
      match.curriculumId ||
      match.course_id ||
      match.courseId,
  );

  const parsedFromCourseText = parseGradeSubjectFromText(
    rawEntry.course_unit_lesson ||
      rawEntry.courseUnitLesson ||
      rawEntry.course ||
      rawEntry.courseLabel ||
      rawEntry.key,
  );

  const subject =
    toSafeString(rawEntry.subject || match.subject)
    || toSafeString(parsedFromCourseText.subject);
  const grade =
    toSafeString(rawEntry.grade || match.grade)
    || toSafeString(parsedFromCourseText.grade);

  const normalizedCurriculumId = normalizeToken(curriculumId);
  const normalizedSubject = normalizeToken(subject);
  const normalizedGrade = normalizeToken(grade);

  const hasCurriculumMatcher = !!normalizedCurriculumId;
  const hasGradeSubjectMatcher = !!(normalizedGrade && normalizedSubject);
  if (!hasCurriculumMatcher && !hasGradeSubjectMatcher) return null;

  const priorityValue = Number(rawEntry.priority);
  const priority = Number.isFinite(priorityValue) ? priorityValue : 1000;

  return {
    id: toSafeString(rawEntry.id) || `entry_${index + 1}`,
    priority,
    _index: index,
    title: toSafeString(rawEntry.title),
    description: toSafeString(rawEntry.description || rawEntry.subtitle),
    videoInput,
    curriculumId,
    grade,
    subject,
    normalizedCurriculumId,
    normalizedGrade,
    normalizedSubject,
  };
}

export function parseCourseEntryVideosConfig(rawConfig) {
  const raw = toSafeString(rawConfig);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    const entries = normalizeRawEntries(parsed)
      .map((entry, index) => normalizeVideoEntry(entry, index))
      .filter(Boolean)
      .sort((a, b) => {
        if (a.priority !== b.priority) return a.priority - b.priority;
        return a._index - b._index;
      });

    return entries;
  } catch (error) {
    console.warn('[courseEntryVideos] Failed to parse Remote Config JSON', {
      message: error?.message || String(error),
    });
    return [];
  }
}

export function resolveCourseEntryVideo(entries, context = {}) {
  if (!Array.isArray(entries) || !entries.length) return null;

  const normalizedCurriculumId = normalizeToken(context.curriculumId);
  const normalizedGrade = normalizeToken(context.grade);
  const normalizedSubject = normalizeToken(context.subject);

  if (normalizedCurriculumId) {
    const matchByCurriculum = entries.find(
      entry => entry.normalizedCurriculumId === normalizedCurriculumId,
    );

    if (matchByCurriculum) {
      return {
        ...matchByCurriculum,
        scopeKey: `curriculum:${normalizedCurriculumId}`,
        matchType: 'curriculum',
      };
    }
  }

  if (normalizedGrade && normalizedSubject) {
    const matchByGradeAndSubject = entries.find(
      entry =>
        entry.normalizedGrade === normalizedGrade &&
        entry.normalizedSubject === normalizedSubject,
    );

    if (matchByGradeAndSubject) {
      return {
        ...matchByGradeAndSubject,
        scopeKey: `subject_grade:${normalizedSubject}:${normalizedGrade}`,
        matchType: 'subject_grade',
      };
    }
  }

  return null;
}

export function resolveCourseEntryVideoFromRemoteConfig(context = {}) {
  const webValue = getRemoteConfigValue(COURSE_ENTRY_VIDEOS_RC_KEY_WEB);
  const mobileValue = getRemoteConfigValue(COURSE_ENTRY_VIDEOS_RC_KEY_MOBILE);
  const rawConfig =
    (webValue?.asString?.() ?? '').trim() ||
    (mobileValue?.asString?.() ?? '').trim() ||
    '';

  const entries = parseCourseEntryVideosConfig(rawConfig);
  return resolveCourseEntryVideo(entries, context);
}

export function getCourseEntryVideoSeenStorageKey({ userId, scopeKey }) {
  const normalizedScope = normalizeStorageToken(scopeKey, 'unknown');
  const normalizedUser = normalizeStorageToken(userId, 'anon');
  return `${COURSE_ENTRY_VIDEO_SEEN_STORAGE_PREFIX}${normalizedUser}_${normalizedScope}`;
}

export function hasSeenCourseEntryVideo({ userId, scopeKey }) {
  if (typeof window === 'undefined') return false;

  const key = getCourseEntryVideoSeenStorageKey({ userId, scopeKey });
  try {
    return window.localStorage?.getItem(key) === 'true';
  } catch (_) {
    return false;
  }
}

export function markCourseEntryVideoSeen({ userId, scopeKey }) {
  if (typeof window === 'undefined') return;

  const key = getCourseEntryVideoSeenStorageKey({ userId, scopeKey });
  try {
    window.localStorage?.setItem(key, 'true');
  } catch (_) {
    // ignore storage failures (private mode / quota / disabled)
  }
}
